// Schedule drafts for the editor: the edited shape, defaults, the core
// action vocabulary (internal/actions) for endpoints that haven't reported
// capabilities yet, and the edit-conflict error (SCH-013).
import type { Endpoint } from './controlApi';
import type { ScheduleAction, ScheduleData } from './scheduleApi';

/** A schedule as edited: actions may be empty until saved. */
export type DraftSchedule = Omit<ScheduleData, 'actions'> & { actions: ScheduleAction[] };

/** Thrown by onSave when someone else changed the schedule meanwhile. */
export class EditConflict extends Error {
  constructor(readonly by: string) {
    super(`${by} changed this schedule while you were editing it.`);
    this.name = 'EditConflict';
  }
}

export interface ParamSpec {
  name: string;
  type: 'id' | 'integer' | 'number' | 'rgb';
  required?: boolean;
  min?: number;
  max?: number;
  ref?: 'presets' | 'scenes' | 'groups';
  label?: string;
}

export interface ActionSpec {
  name: string;
  label?: string;
  params: ParamSpec[];
}

const FADE: ParamSpec = { name: 'fade', type: 'number', min: 0, max: 3600, label: 'Fade (s)' };

/** The core actions (internal/actions), for endpoints that haven't reported yet. */
export const CORE_ACTIONS: ActionSpec[] = [
  {
    name: 'preset.activate',
    label: 'Activate preset',
    params: [{ name: 'preset', type: 'id', required: true, ref: 'presets' }],
  },
  {
    name: 'preset.release',
    label: 'Release preset',
    params: [{ name: 'preset', type: 'id', required: true, ref: 'presets' }, FADE],
  },
  {
    name: 'scene.activate',
    label: 'Activate scene',
    params: [{ name: 'scene', type: 'id', required: true, ref: 'scenes' }],
  },
  {
    name: 'scene.release',
    label: 'Release scene',
    params: [{ name: 'scene', type: 'id', required: true, ref: 'scenes' }, FADE],
  },
  {
    name: 'group.set',
    label: 'Set group',
    params: [
      { name: 'group', type: 'id', required: true, ref: 'groups' },
      { name: 'intensity', type: 'integer', min: 0, max: 255, label: 'Intensity' },
      FADE,
    ],
  },
];

export function actionsOf(ep: Endpoint | undefined): ActionSpec[] {
  const reported = ep?.state?.capabilities.actions as ActionSpec[] | undefined;
  return reported?.length ? reported.filter((a) => Array.isArray(a.params)) : CORE_ACTIONS;
}

export function newSchedule(date: string, endpoints: Endpoint[]): DraftSchedule {
  const ep = endpoints[0];
  return {
    name: '',
    enabled: true,
    rule: {
      trigger_type: 'standard',
      local_time: '19:00',
      astro_offset_minutes: 0,
      start_date: date,
      repeat_pattern: 'daily',
      repeat_interval: 1,
      monthly_mode: 'day_of_month',
      excluded_dates: [],
      exclude_holidays: true,
      holiday_only: false,
    },
    actions: ep ? [{ endpoint_id: ep.id, action: 'preset.activate', params: {} }] : [],
    misfire: { policy: 'fire_if_within', within_minutes: 15 },
    priority: 100,
  };
}
